import { Form, Grid, Select, TextField } from '@approvalmax/ui/src/components';
import { constants, statics } from 'modules/common';
import { type FC, memo } from 'react';
import bemFactory from 'react-bem-factory';

import { qaPrefix } from '../../QBooksCounterpartyCreationPopupContent.constants';
import { FormFieldId } from '../../QBooksCounterpartyCreationPopupContent.types';
import { messages } from './ContactInformationSection.messages';
import type { ContactInformationSectionProps } from './ContactInformationSection.types';

const { qBooksConstants } = constants;
const qa = bemFactory.qa(qaPrefix);

export const ContactInformationSection: FC<ContactInformationSectionProps> = memo((props) => {
    const { withCurrency } = props;

    return (
        <Form.Part>
            <Grid gap={24}>
                <Grid gridTemplateColumns='1fr 1fr' gap={16}>
                    <TextField
                        name={FormFieldId.displayName}
                        label={messages.displayNameAs}
                        qa={qa('display-name-as')}
                        placeholder={messages.displayNameAsPlaceholder}
                        initFocus
                        required
                        maxLength={qBooksConstants.DISPLAY_NAME_MAX_LENGTH}
                    />

                    <TextField
                        name={FormFieldId.companyName}
                        label={messages.companyName}
                        qa={qa('company-name')}
                        placeholder={messages.companyNamePlaceholder}
                        maxLength={qBooksConstants.COMPANY_NAME_MAX_LENGTH}
                    />
                </Grid>

                <Grid gridTemplateColumns='1fr 3fr 3fr 3fr 1fr' gap={16}>
                    <TextField
                        name={FormFieldId.title}
                        label={messages.title}
                        qa={qa('title')}
                        placeholder={messages.titlePlaceholder}
                        maxLength={qBooksConstants.TITLE_MAX_LENGTH}
                    />

                    <TextField
                        name={FormFieldId.givenName}
                        label={messages.givenName}
                        qa={qa('given-name')}
                        placeholder={messages.givenNamePlaceholder}
                        maxLength={qBooksConstants.FIRST_NAME_MAX_LENGTH}
                    />

                    <TextField
                        name={FormFieldId.middleName}
                        label={messages.middleName}
                        qa={qa('middle-name')}
                        placeholder={messages.middleNamePlaceholder}
                        maxLength={qBooksConstants.MIDDLE_NAME_MAX_LENGTH}
                    />

                    <TextField
                        name={FormFieldId.familyName}
                        label={messages.familyName}
                        qa={qa('family-name')}
                        placeholder={messages.familyNamePlaceholder}
                        maxLength={qBooksConstants.LAST_NAME_MAX_LENGTH}
                    />

                    <TextField
                        name={FormFieldId.suffix}
                        label={messages.suffix}
                        qa={qa('suffix')}
                        placeholder={messages.suffixPlaceholder}
                        maxLength={qBooksConstants.SUFFIX_MAX_LENGTH}
                    />
                </Grid>

                <Grid gridTemplateColumns='1fr 1fr' gap={16}>
                    <TextField
                        name={FormFieldId.email}
                        label={messages.email}
                        qa={qa('email')}
                        placeholder={messages.emailPlaceholder}
                        maxLength={qBooksConstants.EMAIL_ADDRESS_MAX_LENGTH}
                        type='email'
                    />

                    <TextField
                        name={FormFieldId.website}
                        label={messages.website}
                        qa={qa('website')}
                        placeholder={messages.websitePlaceholder}
                        maxLength={qBooksConstants.WEBSITE_MAX_LENGTH}
                        type='url'
                    />

                    <TextField
                        name={FormFieldId.primaryPhone}
                        label={messages.primaryPhone}
                        qa={qa('primary-phone')}
                        placeholder={messages.primaryPhonePlaceholder}
                        maxLength={qBooksConstants.PHONE_MAX_LENGTH}
                        type='tel'
                    />

                    <TextField
                        name={FormFieldId.mobilePhone}
                        label={messages.mobilePhone}
                        qa={qa('mobile-phone')}
                        placeholder={messages.mobilePhonePlaceholder}
                        maxLength={qBooksConstants.MOBILE_MAX_LENGTH}
                        type='tel'
                    />

                    <TextField
                        name={FormFieldId.fax}
                        label={messages.fax}
                        qa={qa('fax')}
                        maxLength={qBooksConstants.FAX_MAX_LENGTH}
                        placeholder={messages.faxPlaceholder}
                    />

                    {withCurrency && (
                        <Select
                            name={FormFieldId.currencyId}
                            label={messages.currency}
                            qa={qa('currency')}
                            placeholder={messages.currencyPlaceholder}
                            items={statics.currency.currencyReferences}
                            itemNameKey='text'
                            autocomplete
                        />
                    )}
                </Grid>
            </Grid>
        </Form.Part>
    );
});

ContactInformationSection.displayName = 'ContactInformationSection';
